<template>
  <Modal :value="value" width="lg" @input="$emit('input', $event)">
    <span slot="header">Resultado do cadastro em lote</span>

    <div class="result-cards">
      <div class="result-card result-card--new">
        <span class="result-card__dot"></span>
        <span class="result-card__label">Cadastrados</span>
        <span class="result-card__count">{{ report.new_count || 0 }}</span>
      </div>

      <div class="result-card result-card--updated">
        <span class="result-card__dot"></span>
        <span class="result-card__label">Atualizados</span>
        <span class="result-card__count">{{ report.updated_count || 0 }}</span>
      </div>

      <div class="result-card result-card--errors">
        <span class="result-card__dot"></span>
        <span class="result-card__label">Recusados</span>
        <span class="result-card__count">{{ report.error_count || 0 }}</span>
      </div>
    </div>

    <p v-if="duplicatesRemoved > 0" class="duplicates-info">
      <span v-if="duplicatesRemoved === 1">
        1 linha duplicada foi descartada na leitura do arquivo
      </span>
      <span v-else>
        {{ duplicatesRemoved }} linhas duplicadas foram descartadas na leitura do arquivo
      </span>
      <InfoIcon
        size="1x"
        class="duplicates-info__icon"
        v-tooltip.top="'Linhas com o mesmo Renavam são descartadas na leitura do arquivo, mantendo apenas o primeiro registro de cada Renavam.'"
      />
    </p>

    <div class="table-scroll mt-3">
      <table class="table table-borderless table-border table-align table-h51">
        <thead>
        <tr>
          <th class="nowrap">Linha</th>
          <th class="nowrap">Placa</th>
          <th class="nowrap">Renavam</th>
          <th class="nowrap">Situação</th>
          <th>Motivo</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, index) in items" :key="index">
          <td>{{ item.line }}</td>
          <td>{{ item.plate }}</td>
          <td>{{ item.renavam }}</td>
          <td>
            <span v-if="item.status === 'rejected'" class="error-badge">{{ statusLabel(item) }}</span>
            <span v-else class="valid-badge">{{ statusLabel(item) }}</span>
          </td>
          <td>{{ reason(item) }}</td>
        </tr>
        <tr v-if="!items.length">
          <td colspan="5" class="text-center text-muted py-3">
            Nenhum veículo processado.
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <template slot="footer">
      <button class="btn btn-outline mr-2" @click="$emit('input', false)">Fechar</button>
      <button class="btn btn-primary" :disabled="!items.length" @click="downloadDetails">
        Baixar detalhes
      </button>
    </template>
  </Modal>
</template>

<script>
  import Modal from '@/components/shares/specialTag/Modal'
  import { InfoIcon } from 'vue-feather-icons'
  import { buildCsvBlob } from '@/models/csvFile'

  const STATUS_LABELS = { created: 'Cadastrado', updated: 'Atualizado', rejected: 'Recusado' }

  export default {
    components: { InfoIcon, Modal },
    name: 'BatchVehiclesResultModal',
    props: {
      value: { default: false, type: Boolean },
      report: { default: () => ({}), type: Object },
    },
    computed: {
      items() {
        return this.report.items || []
      },
      duplicatesRemoved() {
        return this.report.duplicates_removed || 0
      },
    },
    methods: {
      statusLabel(item) {
        return STATUS_LABELS[item.status] || item.status
      },
      reason(item) {
        const messages = item.messages || []
        return messages.length ? messages.join(' | ') : '-'
      },
      downloadDetails() {
        const header = ['Linha', 'Placa', 'Renavam', 'Situação', 'Motivo']
        const lines = this.items.map(item => [
          item.line,
          item.plate,
          item.renavam,
          this.statusLabel(item),
          this.reason(item),
        ])

        this.$parse.buildDownload('Resultado - Cadastro de veículos em lote.csv', buildCsvBlob([header, ...lines]))
      },
    },
  }
</script>

<style scoped lang="scss">
  .result-cards {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
  }

  .result-card {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #E1E3EB;
    border-radius: 8px;
    border-bottom: 3px solid #E1E3EB;

    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #9aa5b1;
      flex-shrink: 0;
    }

    &__label {
      flex: 1;
      font-size: 0.875rem;
      font-weight: 600;
      color: #051B30;
    }

    &__count {
      font-size: 1.25rem;
      font-weight: 700;
      color: #051B30;
    }

    &--new .result-card__dot { background: #28a745; }
    &--new { border-bottom-color: #28a745; }

    &--updated .result-card__dot { background: #2D98DA; }
    &--updated { border-bottom-color: #2D98DA; }

    &--errors .result-card__dot { background: #E93F66; }
    &--errors { border-bottom-color: #E93F66; }
  }

  .duplicates-info {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    color: #465059;
    margin: 0 0 4px 4px;

    &__icon {
      color: #2D98DA;
      flex-shrink: 0;
      cursor: help;
    }
  }

  .table-scroll {
    overflow-x: auto;
    max-width: 100%;

    table {
      margin-bottom: 0;
    }
  }

  .error-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    background: #fde8ed;
    color: #E93F66;
  }

  .valid-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    background: #e4f5e8;
    color: #28a745;
  }
</style>
